/**
 * Packmaxu product page behaviour (layout/packmaxu.liquid).
 * Vanilla JS, no dependencies. Every init is idempotent so the theme editor can re-run it
 * after a section reloads.
 */
(() => {
  const PM = (window.PM = window.PM || {});
  const routes = PM.routes || { cartAdd: '/cart/add', cartChange: '/cart/change', cart: '/cart' };
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const HEADER_OFFSET = 64;

  /* ------------------------------------------------------------------ money */
  function formatMoney(cents, format = PM.moneyFormat || '€{{amount}}') {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    format = format.replace(/<[^>]*>/g, '').trim(); // some stores wrap the format in <span class=money>
    const fmt = (n, precision, thousands = ',', decimal = '.') => {
      if (isNaN(n) || n == null) return '0';
      const [whole, frac] = (n / 100).toFixed(precision).split('.');
      return whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`) + (frac ? decimal + frac : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount': return fmt(cents, 2);
        case 'amount_no_decimals': return fmt(cents, 0);
        case 'amount_with_comma_separator': return fmt(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(cents, 2, "'", '.');
        case 'amount_with_space_separator': return fmt(cents, 2, ' ', ',');
        default: return fmt(cents, 2);
      }
    });
  }
  const moneyNoZeros = (cents) => formatMoney(cents).replace(/[.,]00(?!\d)/, '');

  /* -------------------------------------------------------------------- eta */
  function addBusinessDays(date, n) {
    const d = new Date(date);
    let k = 0;
    while (k < n) {
      d.setDate(d.getDate() + 1);
      const w = d.getDay();
      if (w !== 0 && w !== 6) k++;
    }
    return d;
  }
  function renderEta(root = document) {
    const host = $('[data-pm-product]');
    const min = Number(host?.dataset.etaMin) || 6;
    const max = Math.max(min, Number(host?.dataset.etaMax) || 11);
    const lang = (PM.locale || 'en').startsWith('en') ? 'en-GB' : PM.locale;
    const now = new Date();
    const a = addBusinessDays(now, min);
    const b = addBusinessDays(now, max);
    const long = (d) => d.toLocaleDateString(lang, { weekday: 'short', day: 'numeric', month: 'short' });
    const mon = (d) => d.toLocaleDateString(lang, { month: 'short' });
    const text = {
      long: `${long(a)} – ${long(b)}`,
      short: a.getMonth() === b.getMonth() ? `${a.getDate()}–${b.getDate()} ${mon(b)}` : `${a.getDate()} ${mon(a)} – ${b.getDate()} ${mon(b)}`,
    };
    $$('[data-pm-eta]', root).forEach((el) => (el.textContent = text[el.dataset.pmEta] || text.long));
  }

  /* ----------------------------------------------------------------- scroll */
  function scrollToHash(hash) {
    const id = decodeURIComponent(hash.replace(/^#/, ''));
    const el = id && document.getElementById(id);
    if (!el) return false;
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET, behavior: reduced ? 'auto' : 'smooth' });
    return true;
  }

  /* ---------------------------------------------------------------- drawers */
  let openDrawer = null;
  let lastFocus = null;
  function setTriggers(id, expanded) {
    $$(`[data-pm-drawer-open="${id}"]`).forEach((t) => t.setAttribute('aria-expanded', String(expanded)));
  }
  function showDrawer(id, trigger) {
    const root = document.getElementById(id);
    if (!root) return;
    if (openDrawer && openDrawer !== root) hideDrawer(openDrawer, false);
    lastFocus = trigger || document.activeElement;
    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('pm-lock');
    setTriggers(id, true);
    openDrawer = root;
    const panel = $('.pm-drawer', root);
    requestAnimationFrame(() => ($('[data-pm-drawer-close]', panel) || panel).focus({ preventScroll: true }));
    document.dispatchEvent(new CustomEvent('pm:drawer', { detail: { id, open: true } }));
  }
  function hideDrawer(root = openDrawer, restoreFocus = true) {
    if (!root) return;
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('pm-lock');
    setTriggers(root.id, false);
    if (openDrawer === root) openDrawer = null;
    if (restoreFocus && lastFocus && document.contains(lastFocus)) lastFocus.focus({ preventScroll: true });
    document.dispatchEvent(new CustomEvent('pm:drawer', { detail: { id: root.id, open: false } }));
  }
  document.addEventListener('keydown', (e) => {
    if (!openDrawer) return;
    if (e.key === 'Escape') return hideDrawer();
    if (e.key !== 'Tab') return;
    const focusables = $$('a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])', $('.pm-drawer', openDrawer)).filter((el) => el.offsetParent !== null);
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  /* ------------------------------------------------------------------- cart */
  const cartSection = PM.cartSection || 'pm-cart-drawer';

  function renderCart(sections) {
    const html = sections && sections[cartSection];
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = $('[data-pm-cart-content]', doc);
    const current = $('[data-pm-cart-content]');
    if (next && current) {
      current.innerHTML = next.innerHTML;
      current.dataset.count = next.dataset.count;
      renderEta(current);
    }
    updateCount(Number(next?.dataset.count || 0));
  }
  function updateCount(count) {
    $$('[data-pm-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }
  async function cartRequest(url, body) {
    const isForm = body instanceof FormData;
    const res = await fetch(url, {
      method: 'POST',
      headers: isForm ? { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: isForm ? body : JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.status) throw new Error(json.description || json.message || 'Something went wrong. Please try again.');
    return json;
  }
  async function addToCart(formData) {
    formData.append('sections', cartSection);
    formData.append('sections_url', window.location.pathname);
    const json = await cartRequest(`${routes.cartAdd}.js`, formData);
    renderCart(json.sections);
    return json;
  }
  async function changeLine(key, quantity) {
    const drawer = $('[data-pm-cart-drawer] .pm-drawer');
    drawer?.classList.add('is-loading');
    try {
      const json = await cartRequest(`${routes.cartChange}.js`, { id: key, quantity, sections: [cartSection], sections_url: window.location.pathname });
      renderCart(json.sections);
    } catch (err) {
      const box = $('[data-pm-cart-error]');
      if (box) { box.textContent = err.message; box.hidden = false; }
    } finally {
      drawer?.classList.remove('is-loading');
    }
  }
  PM.cart = { add: addToCart, change: changeLine, open: () => showDrawer('pm-cart-drawer') };

  /* ------------------------------------------------------ delegated clicks */
  document.addEventListener('click', (e) => {
    const t = e.target;

    const opener = t.closest('[data-pm-drawer-open]');
    if (opener) {
      const id = opener.dataset.pmDrawerOpen;
      if (document.getElementById(id)) { e.preventDefault(); showDrawer(id, opener); }
      return;
    }

    const overlay = t.closest('[data-pm-drawer-overlay]');
    if (overlay && t === overlay) return hideDrawer();

    const scrollLink = t.closest('a[data-pm-scroll]');
    const closer = t.closest('[data-pm-drawer-close]');
    if (closer) {
      const inDrawer = openDrawer;
      hideDrawer(inDrawer, !scrollLink);
      if (!scrollLink) return;
    }
    if (scrollLink) {
      const url = new URL(scrollLink.href, window.location.href);
      if (url.pathname === window.location.pathname && url.hash && scrollToHash(url.hash)) e.preventDefault();
      return;
    }

    const lineBtn = t.closest('[data-pm-line-qty]');
    if (lineBtn) {
      const item = lineBtn.closest('[data-key]');
      if (item) changeLine(item.dataset.key, Number(lineBtn.dataset.pmLineQty));
      return;
    }

    const trigger = t.closest('.pm-accordion__trigger');
    if (trigger) {
      const item = trigger.closest('[data-pm-accordion]');
      if (!item) return;
      const open = !item.classList.contains('is-open');
      item.classList.toggle('is-open', open);
      trigger.setAttribute('aria-expanded', String(open));
    }
  });

  /* ------------------------------------------------------------ header */
  function initHeader() {
    const header = $('[data-pm-header]');
    if (!header || header.dataset.pmInit) return;
    header.dataset.pmInit = '1';
    const cta = $('[data-pm-header-cta]', header);
    if (cta && !document.getElementById('buy')) header.classList.add('show-cta');
  }

  /* ------------------------------------------------------------- gallery */
  function initGallery(root) {
    const track = $('[data-pm-gallery-track]', root);
    if (!track || track.dataset.pmInit) return null;
    track.dataset.pmInit = '1';
    const slides = $$('.pm-gallery__slide', track);
    const thumbs = $$('[data-pm-gallery-thumb]', root);
    const caption = $('[data-pm-gallery-caption]', root);
    let index = 0;

    const paint = (i) => {
      index = i;
      thumbs.forEach((b, k) => b.setAttribute('aria-selected', String(k === i)));
      if (caption) caption.textContent = `${i + 1} / ${slides.length}`;
      slides.forEach((s, k) => { if (k !== i) $$('video', s).forEach((v) => v.pause()); });
    };
    const goTo = (i, smooth = true) => {
      const n = slides.length;
      if (!n) return;
      i = ((i % n) + n) % n;
      track.scrollTo({ left: i * track.clientWidth, behavior: smooth && !reduced ? 'smooth' : 'auto' });
      paint(i);
    };
    let raf = 0;
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
        if (i !== index) paint(i);
      });
    }, { passive: true });
    track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); goTo(index + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(index - 1); }
    });
    $('[data-pm-gallery-prev]', root)?.addEventListener('click', () => goTo(index - 1));
    $('[data-pm-gallery-next]', root)?.addEventListener('click', () => goTo(index + 1));
    thumbs.forEach((b) => b.addEventListener('click', () => goTo(Number(b.dataset.pmGalleryThumb))));
    paint(0);

    return {
      goToMedia(id) {
        const i = slides.findIndex((s) => s.dataset.mediaId === String(id));
        if (i >= 0 && i !== index) goTo(i);
      },
    };
  }

  /* ------------------------------------------------------------- product */
  const PLUG_NAMES = { EU: 'Europe (type C/F)', UK: 'United Kingdom (type G)', US: 'United States (type A/B)', AU: 'Australia (type I)' };
  function guessPlug() {
    const l = (navigator.language || '').toLowerCase();
    if (l.endsWith('-gb') || l.endsWith('-ie')) return 'UK';
    if (l.endsWith('-us') || l.endsWith('-ca')) return 'US';
    if (l.endsWith('-au') || l.endsWith('-nz')) return 'AU';
    return 'EU';
  }
  const plugCode = (v) => String(v).trim().split(/\s+/)[0].toUpperCase(); // "EU Plug" -> "EU"
  const optionLabel = (v) => PLUG_NAMES[plugCode(v)] || v;

  function initProduct(section) {
    if (section.dataset.pmInit) return;
    section.dataset.pmInit = '1';
    let product;
    try { product = JSON.parse($('[data-pm-product-json]', section).textContent); } catch (e) { return; }
    const form = $('[data-pm-form]', section);
    if (!form) return;
    const idInput = $('[data-pm-variant-id]', form);
    const qtyInput = $('[data-pm-qty]', form);
    const addBtn = $('[data-pm-add-btn]', form);
    const errorBox = $('[data-pm-form-error]', form);
    const fieldsets = $$('[data-pm-option]', form);
    const ctaText = section.dataset.cta || 'Add to cart';
    const gallery = initGallery(section);
    let variant = product.variants.find((v) => String(v.id) === idInput.value) || product.variants[0];

    const selectedOptions = () => fieldsets.map((fs) => $('input:checked', fs)?.value);
    const findVariant = (opts) => product.variants.find((v) => v.options.every((o, i) => o === opts[i]));

    function paintPrices() {
      const qty = Number(qtyInput?.value) || 1;
      $$('[data-pm-price-block]').forEach((block) => {
        if (!variant) return;
        const q = Number(block.dataset.qty) || 1;
        const now = variant.price * q;
        const was = (variant.compare_at_price || 0) * q;
        $('[data-pm-price]', block).textContent = formatMoney(now);
        const cmp = $('[data-pm-compare]', block);
        const save = $('[data-pm-save]', block);
        const onSale = was > now;
        if (cmp) { cmp.hidden = !onSale; cmp.textContent = onSale ? formatMoney(was) : ''; }
        if (save) { save.hidden = !onSale; save.textContent = onSale ? `Save ${moneyNoZeros(was - now)}` : ''; }
      });
      if (variant) {
        $$('[data-pm-price]').forEach((el) => { if (!el.closest('[data-pm-price-block]')) el.textContent = formatMoney(variant.price); });
        $$('[data-pm-installment]').forEach((el) => (el.textContent = formatMoney(Math.floor(variant.price / (Number(el.dataset.pmInstallment) || 3)))));
      }
      const total = $('[data-pm-line-total]', form);
      if (total && variant) total.textContent = formatMoney(variant.price * qty);
    }

    function paintAvailability() {
      const available = !!(variant && variant.available);
      const label = !variant ? 'Unavailable' : available ? ctaText : 'Sold out';
      $$('[data-pm-add-label]').forEach((el) => (el.textContent = label));
      [addBtn, ...$$('[data-pm-add-trigger]')].forEach((b) => b && (b.disabled = !available));
      // Grey out values that have no available variant given the other selections.
      const current = selectedOptions();
      fieldsets.forEach((fs, i) => {
        $$('input', fs).forEach((input) => {
          const opts = current.slice();
          opts[i] = input.value;
          const v = findVariant(opts);
          input.closest('.pm-pill')?.classList.toggle('is-unavailable', !v || !v.available);
        });
      });
    }

    function onVariantChange(updateUrl = true, moveGallery = true) {
      const opts = selectedOptions();
      variant = findVariant(opts);
      fieldsets.forEach((fs, i) => {
        const cap = $('[data-pm-option-caption]', fs);
        if (cap) cap.textContent = optionLabel(opts[i]);
      });
      if (variant) {
        idInput.value = variant.id;
        $$('[data-pm-variant-title]').forEach((el) => (el.textContent = variant.title));
        if (moveGallery && variant.featured_media && gallery) gallery.goToMedia(variant.featured_media.id);
        if (updateUrl && section.dataset.url && window.location.pathname.endsWith(section.dataset.url)) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }
      }
      if (errorBox) errorBox.textContent = '';
      paintPrices();
      paintAvailability();
    }

    fieldsets.forEach((fs) => fs.addEventListener('change', () => onVariantChange()));

    // Pre-select the shopper's plug type from their browser locale (only when no variant is in the URL).
    if (!new URL(window.location.href).searchParams.has('variant')) {
      fieldsets.forEach((fs) => {
        const guess = guessPlug();
        const input = $$('input', fs).find((i) => plugCode(i.value) === guess);
        if (!input || input.checked) return;
        const opts = selectedOptions();
        opts[Number(fs.dataset.pmOption)] = input.value;
        const v = findVariant(opts);
        if (v && v.available) { input.checked = true; onVariantChange(false); }
      });
    }

    // Quantity stepper
    const steps = $$('[data-pm-qty-step]', form);
    const clampQty = () => {
      const min = Number(qtyInput.min) || 1;
      const max = Number(qtyInput.max) || 99;
      const n = Math.min(max, Math.max(min, Math.round(Number(qtyInput.value) || min)));
      qtyInput.value = n;
      steps.forEach((b) => (b.disabled = Number(b.dataset.pmQtyStep) < 0 ? n <= min : n >= max));
      paintPrices();
    };
    steps.forEach((b) => b.addEventListener('click', () => { qtyInput.value = Number(qtyInput.value) + Number(b.dataset.pmQtyStep); clampQty(); }));
    qtyInput?.addEventListener('change', clampQty);
    if (qtyInput) clampQty();

    // Add to cart
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!variant || !variant.available || addBtn.getAttribute('aria-busy') === 'true') return;
      const busy = [addBtn, ...$$('[data-pm-add-trigger]')];
      busy.forEach((b) => b.setAttribute('aria-busy', 'true'));
      if (errorBox) errorBox.textContent = '';
      try {
        await addToCart(new FormData(form));
        showDrawer('pm-cart-drawer', document.activeElement);
      } catch (err) {
        if (errorBox) errorBox.textContent = err.message;
        if (document.activeElement !== addBtn) scrollToHash('#buy');
      } finally {
        busy.forEach((b) => b.removeAttribute('aria-busy'));
      }
    });
    $$('[data-pm-add-trigger]').forEach((b) => {
      if (b.dataset.pmBound) return;
      b.dataset.pmBound = '1';
      b.addEventListener('click', () => {
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.dispatchEvent(new Event('submit', { cancelable: true }));
      });
    });

    onVariantChange(false, false);
    initBuyBar(section);
  }

  /* -------------------------------------- sticky buy bar + header state */
  function initBuyBar(section) {
    const anchor = $('[data-pm-buy-anchor]', section);
    const bar = $('[data-pm-buybar]', section);
    let cartOpen = false;
    let raf = 0;
    const update = () => {
      raf = 0;
      const header = $('[data-pm-header]');
      const footer = $('.pm-footer');
      const desktop = window.innerWidth >= 1024;
      if (header) header.classList.toggle('is-solid', window.scrollY > 8 || !desktop);
      if (!anchor) return;
      const r = anchor.getBoundingClientRect();
      const buyAbove = r.bottom < HEADER_OFFSET;
      const buyInView = r.top < window.innerHeight && r.bottom > 0;
      const footerIn = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
      if (header) header.classList.toggle('show-cta', desktop && buyAbove && !buyInView);
      if (bar) {
        const visible = buyAbove && !footerIn && !cartOpen;
        bar.classList.toggle('is-visible', visible);
        bar.setAttribute('aria-hidden', String(!visible));
        $$('button', bar).forEach((b) => (b.tabIndex = visible ? 0 : -1));
      }
    };
    const schedule = () => { if (!raf) raf = requestAnimationFrame(update); };
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    document.addEventListener('pm:drawer', (e) => { if (e.detail.id === 'pm-cart-drawer') { cartOpen = e.detail.open; schedule(); } });
    update();
  }

  /* ---------------------------------------------------------------- tabs */
  function initTabs(root) {
    if (root.dataset.pmInit) return;
    root.dataset.pmInit = '1';
    const tabs = $$('[data-pm-tab]', root);
    const panels = $$('[data-pm-panel]', root);
    const select = (i, focus) => {
      tabs.forEach((t, k) => { t.setAttribute('aria-selected', String(k === i)); t.tabIndex = k === i ? 0 : -1; });
      panels.forEach((p, k) => p.classList.toggle('is-active', k === i));
      if (focus) tabs[i]?.focus();
    };
    tabs.forEach((t, i) => {
      t.addEventListener('click', () => select(i));
      t.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') select((i + 1) % tabs.length, true);
        if (e.key === 'ArrowLeft') select((i - 1 + tabs.length) % tabs.length, true);
      });
    });
    root.pmSelect = select;
  }

  /* --------------------------------------------------------------- video */
  function initVideo(root) {
    if (root.dataset.pmInit) return;
    root.dataset.pmInit = '1';
    const v = $('[data-pm-video-el]', root);
    if (!v) return;
    v.muted = true;
    let loaded = false;
    let userPaused = false;
    let hlsReady = null;
    const hlsUrl = root.dataset.hls;

    const load = () => {
      if (loaded) return hlsReady || Promise.resolve();
      loaded = true;
      const sources = $$('source[data-src]', v);
      if (sources.length) {
        sources.forEach((s) => (s.src = s.dataset.src));
        v.load();
        return Promise.resolve();
      }
      if (!hlsUrl) return Promise.resolve();
      if (v.canPlayType('application/vnd.apple.mpegurl')) { v.src = hlsUrl; return Promise.resolve(); }
      hlsReady = new Promise((resolve) => {
        const s = document.createElement('script');
        s.src = 'https://cdn.jsdelivr.net/npm/hls.js@1.5.13/dist/hls.min.js';
        s.onload = () => {
          if (window.Hls && window.Hls.isSupported()) {
            const h = new window.Hls();
            h.loadSource(hlsUrl);
            h.attachMedia(v);
          }
          resolve();
        };
        s.onerror = resolve;
        document.head.appendChild(s);
      });
      return hlsReady;
    };
    const paint = () => {
      root.classList.toggle('is-playing', !v.paused);
      root.classList.toggle('is-unmuted', !v.muted);
      $('[data-pm-video-mute]', root)?.setAttribute('aria-label', v.muted ? 'Turn sound on' : 'Turn sound off');
    };
    v.addEventListener('play', paint);
    v.addEventListener('pause', paint);
    v.addEventListener('volumechange', paint);

    $('[data-pm-video-toggle]', root)?.addEventListener('click', async () => {
      if (v.paused) { userPaused = false; await load(); v.play().catch(() => {}); }
      else { userPaused = true; v.pause(); }
    });
    $('[data-pm-video-mute]', root)?.addEventListener('click', () => { v.muted = !v.muted; });

    if (!('IntersectionObserver' in window)) { load(); return; }
    new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) load();
      if (entry.isIntersecting && entry.intersectionRatio >= 0.4) {
        if (!reduced && !userPaused && v.paused) load().then(() => v.play().catch(() => {}));
      } else if (!v.paused) v.pause();
    }, { threshold: [0, 0.4, 0.7], rootMargin: '200px 0px 0px 0px' }).observe($('.pm-video__frame', root) || v);
  }

  /* ------------------------------------------------------- 360 rotation */
  function initRotation(root) {
    if (root.dataset.pmInit) return;
    root.dataset.pmInit = '1';
    const range = $('[data-pm-rot-range]', root);
    const stops = $$('[data-pm-rot-stop]', root);
    const beam = $('[data-pm-rot-beam]', root);
    const seg = $('[data-pm-rot-seg]', root);
    const proj = $('[data-pm-rot-proj]', root);
    const diagram = $('[data-pm-rot-diagram]', root);
    const P = { x: 0.96, y: 2.4 }, wallX = 3.68, ceilY = 0.36, floorY = 2.52;
    const px = (x) => (x / 4 * 100).toFixed(2) + '%';
    const py = (y) => (y / 3 * 100).toFixed(2) + '%';
    const cl = (v, a, b) => Math.max(a, Math.min(b, v));

    const render = (tilt) => {
      const th = tilt * Math.PI / 180, c = Math.cos(th), sn = Math.sin(th);
      const Lw = c > 1e-4 ? (wallX - P.x) / c : Infinity;
      const Lc = sn > 1e-4 ? (P.y - ceilY) / sn : Infinity;
      const L = Math.min(Lw, Lc), hx = P.x + L * c, hy = P.y - L * sn, half = L * 0.2;
      let A, B, s, surf;
      if (Lw <= Lc) {
        const y1 = cl(hy - half, ceilY, floorY), y2 = cl(hy + half, ceilY, floorY);
        A = [wallX, y1]; B = [wallX, y2];
        s = { left: `calc(${px(wallX)} - 4px)`, top: py(y1), width: '4px', height: ((y2 - y1) / 3 * 100).toFixed(2) + '%' };
        surf = 'Wall';
      } else {
        const x1 = cl(hx - half, 0, wallX), x2 = cl(hx + half, 0, wallX);
        A = [x1, ceilY]; B = [x2, ceilY];
        s = { left: px(x1), top: py(ceilY), width: ((x2 - x1) / 4 * 100).toFixed(2) + '%', height: '4px' };
        surf = 'Ceiling';
      }
      beam.style.clipPath = `polygon(${px(P.x)} ${py(P.y)}, ${px(A[0])} ${py(A[1])}, ${px(B[0])} ${py(B[1])})`;
      Object.assign(seg.style, s);
      proj.style.transform = `rotate(${-tilt}deg)`;
      $('[data-pm-rot-deg]', root).textContent = tilt;
      $('[data-pm-rot-surface]', root).textContent = surf;
      diagram.setAttribute('aria-label', `Diagram: projector tilted ${tilt}°, picture on the ${surf.toLowerCase()}`);
      const sel = tilt === 0 ? 0 : tilt === 90 ? 1 : 2;
      stops.forEach((b, i) => b.setAttribute('aria-checked', String(i === sel)));
      range.value = tilt;
    };
    range.addEventListener('input', () => render(Number(range.value)));
    stops.forEach((b) => b.addEventListener('click', () => render(Number(b.dataset.pmRotStop))));
    render(0);
  }

  /* --------------------------------------------------------- screen size */
  function initSize(root) {
    if (root.dataset.pmInit) return;
    root.dataset.pmInit = '1';
    const TR = Number(root.dataset.throwRatio) > 0 ? Number(root.dataset.throwRatio) : 1.2;
    const tv = Number(root.dataset.tv) || 65;
    const range = $('[data-pm-size-range]', root);
    const pic = $('[data-pm-size-pic]', root);
    const tvBox = $('[data-pm-size-tv]', root);
    const INCH_PER_M = 45.17; // 16:9 diagonal in inches per metre of picture width
    const tvW = tv / INCH_PER_M;
    tvBox.style.width = (tvW / 4 * 100).toFixed(1) + '%';
    tvBox.style.height = (tvW * 25).toFixed(1) + '%';
    const d100 = (100 / INCH_PER_M * TR).toFixed(1);

    const render = () => {
      const dist = Number(range.value);
      const widthM = dist / TR;
      const diag = Math.round(widthM * INCH_PER_M);
      pic.style.width = Math.min(96, widthM / 4 * 100).toFixed(1) + '%';
      pic.style.height = Math.min(88, widthM * 25).toFixed(1) + '%';
      $('[data-pm-size-diag]', root).textContent = diag;
      $('[data-pm-size-dist]', root).textContent = dist.toFixed(1) + ' m';
      $('[data-pm-size-label]', root).textContent = `From ${dist.toFixed(1)} m away. About ${widthM.toFixed(2)} m wide.`;
      $('[data-pm-size-compare]', root).textContent = diag > tv
        ? `That is ${Math.round((diag * diag) / (tv * tv) * 10) / 10}× the screen area of a ${tv}-inch TV. For 100 inches, place it about ${d100} m from the wall.`
        : `Move it back for a bigger picture. For 100 inches, place it about ${d100} m from the wall.`;
      $('[data-pm-size-diagram]', root).setAttribute('aria-label', `Diagram: a ${diag}-inch picture compared with a ${tv}-inch TV outline`);
    };
    range.addEventListener('input', render);
    render();
  }

  /* -------------------------------------------------------------- reviews */
  function initReviews(vp) {
    if (vp.dataset.pmInit) return;
    vp.dataset.pmInit = '1';
    const track = $('[data-pm-reviews-track]', vp);
    const cards = track ? Array.from(track.children) : [];
    const speed = Number(vp.dataset.speed); // px per second
    // Without motion, or when every card already fits, this stays a plain swipeable row.
    if (reduced || !(speed > 0) || !cards.length || track.scrollWidth <= vp.clientWidth) return;

    // A second, identical set makes the loop seamless: after one set has passed, jump back by its width.
    cards.forEach((card) => {
      const clone = card.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      Array.from(clone.attributes).forEach((a) => { if (a.name.startsWith('data-shopify')) clone.removeAttribute(a.name); });
      track.appendChild(clone);
    });
    const loop = track.children[cards.length].offsetLeft - cards[0].offsetLeft;
    if (!(loop > 0)) return;

    let pos = 0;
    let last = 0;
    let raf = 0;
    let visible = false;
    let hover = false;
    let focus = false;
    let touched = false; // finger down, or the visitor scrolled by hand a moment ago
    let resumeTimer = 0;
    const paused = () => hover || focus || touched || !visible || document.hidden;
    const step = (now) => {
      raf = 0;
      if (paused()) return;
      if (last) {
        pos += (speed * Math.min(now - last, 100)) / 1000;
        if (pos >= loop) pos -= loop;
        vp.scrollLeft = pos;
      }
      last = now;
      raf = requestAnimationFrame(step);
    };
    const play = () => {
      if (raf || paused()) return;
      pos = vp.scrollLeft % loop; // pick up from wherever the visitor left the row
      last = 0;
      raf = requestAnimationFrame(step);
    };
    const touch = () => { touched = true; clearTimeout(resumeTimer); };
    const untouch = () => {
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(() => { touched = false; play(); }, 2500);
    };
    vp.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') hover = true; });
    vp.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') { hover = false; play(); } });
    vp.addEventListener('touchstart', touch, { passive: true });
    vp.addEventListener('touchend', untouch, { passive: true });
    vp.addEventListener('touchcancel', untouch, { passive: true });
    vp.addEventListener('wheel', () => { touch(); untouch(); }, { passive: true });
    // Keyboard focus only: a tap or click also focuses the row and must not leave it paused.
    vp.addEventListener('focusin', () => { if (vp.matches(':focus-visible')) focus = true; });
    vp.addEventListener('focusout', () => { focus = false; play(); });
    document.addEventListener('visibilitychange', play);
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; play(); }).observe(vp);
    } else {
      visible = true;
      play();
    }
  }

  /* ---------------------------------------------------------------- boot */
  function init(root = document) {
    initHeader();
    $$('[data-pm-product]', root).forEach(initProduct);
    if (root.matches?.('[data-pm-product]')) initProduct(root);
    $$('[data-pm-tabs]', root).forEach(initTabs);
    $$('[data-pm-video]', root).forEach(initVideo);
    $$('[data-pm-rotation]', root).forEach(initRotation);
    $$('[data-pm-size]', root).forEach(initSize);
    $$('[data-pm-reviews]', root).forEach(initReviews);
    renderEta(document);
    if (!$('[data-pm-product]')) {
      // No purchase module on this page: keep the header solid state in sync on its own.
      const header = $('[data-pm-header]');
      const paint = () => header && header.classList.toggle('is-solid', window.scrollY > 8 || window.innerWidth < 1024);
      window.addEventListener('scroll', paint, { passive: true });
      paint();
    }
  }

  init();
  if (window.location.hash) setTimeout(() => scrollToHash(window.location.hash), 50);

  // Theme editor support
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:block:select', (e) => {
    const block = e.target;
    const tabs = block.closest('[data-pm-tabs]');
    const panel = block.closest('[data-pm-panel]');
    if (tabs && panel && tabs.pmSelect) tabs.pmSelect(Number(panel.dataset.pmPanel));
    if (block.matches('[data-pm-accordion]')) {
      block.classList.add('is-open');
      $('.pm-accordion__trigger', block)?.setAttribute('aria-expanded', 'true');
    }
  });
  document.addEventListener('shopify:section:select', (e) => {
    if (e.target.querySelector('[data-pm-cart-drawer]')) showDrawer('pm-cart-drawer');
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    if (e.target.querySelector('[data-pm-cart-drawer]')) hideDrawer();
  });
})();
